const PrivacyPolicyBody = () => (
  <div className="privacy">
    <p className="privacy-effective">Effective Date: 20 April 2026</p>
    <p className="privacy-brand">Noble Guard Security Co. Ltd.</p>
    <h1 className="priv-head">Privacy, Data Protection &amp; Cookies Policy</h1>

    <p className="priv-body">
      Noble Guard Security Co. Ltd. (hereinafter referred to as &quot;the Company&quot;,
      &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) is a professional security services
      provider incorporated and operating in the Federal Republic of Nigeria. The Company
      provides a comprehensive suite of security solutions, including but not limited to
      manned guarding, mobile patrols, surveillance and monitoring, risk assessments,
      executive protection, security consulting, and incident response services
      (collectively, the &quot;Services&quot;).
    </p>
    <p className="priv-body">
      The Company is committed to protecting the privacy, confidentiality, and security of
      the personal data entrusted to it by its clients, employees, deployed personnel,
      business partners, website visitors, and all other individuals whose information the
      Company processes in the ordinary course of its business operations. This Privacy,
      Data Protection &amp; Cookies Policy (&quot;Policy&quot;) has been prepared in accordance
      with the Nigerian Data Protection Act, 2023 (&quot;NDPA&quot;), the Nigeria Data Protection
      Regulation, 2019 (&quot;NDPR&quot;), Private Guard Companies Regulations (Nigeria) and all
      other applicable laws, regulations, and industry standards governing data protection and
      privacy in Nigeria and international data protection laws.
    </p>
    <p className="priv-body">
      This Policy explains how the Company collects, uses, stores, discloses, transfers, and
      protects personal data, and sets out the rights of data subjects in relation to their
      personal information. By engaging the Company&apos;s Services, accessing the Company&apos;s
      website, or otherwise providing personal data to the Company, you acknowledge that you have
      read, understood, and agreed to the terms of this Policy. This Policy forms an integral part
      of the Company&apos;s Terms and Conditions of Service.
    </p>

    <h2>1. Definitions and interpretation</h2>
    <p className="priv-body">
      For the purposes of this Policy, the following definitions shall apply:
    </p>
    <p className="priv-body">
      <strong>Personal Data:</strong> means any information relating to an identified or
      identifiable natural person, including but not limited to names, identification numbers,
      location data, biometric data, and any factor specific to the physical, physiological,
      genetic, mental, economic, cultural, or social identity of that individual.
    </p>
    <p className="priv-body">
      <strong>Usage Data:</strong> means data automatically generated and collected during the
      use of the Company&apos;s digital platforms and website.
    </p>
    <p className="priv-body">
      <strong>Cookies:</strong> means small text files placed on a user&apos;s device to collect
      standard internet log information and visitor behaviour data.
    </p>
    <p className="priv-body">
      <strong>Data Controller:</strong> means Noble Guard Security Co. Ltd., which determines the
      purposes and means of processing personal data.
    </p>
    <p className="priv-body">
      <strong>Data Processor:</strong> means any third party that processes personal data on behalf
      of the Company, pursuant to a written data processing agreement.
    </p>
    <p className="priv-body">
      <strong>Data Subject / User:</strong> means the individual to whom personal data relates and
      whose rights are protected under this Policy.
    </p>
    <p className="priv-body">
      <strong>Processing:</strong> means any operation performed on personal data, whether or not by
      automated means, including collection, recording, storage, alteration, retrieval, use,
      disclosure, or erasure.
    </p>
    <p className="priv-body">
      <strong>Consent:</strong> means the freely given, specific, informed, and unambiguous
      agreement of the data subject to the processing of their personal data.
    </p>
    <p className="priv-body">
      <strong>NDPA:</strong> means the Nigerian Data Protection Act, 2023, and all subsidiary
      legislation, regulations, and guidelines issued thereunder.
    </p>

    <h2>2. Scope and application</h2>
    <p className="priv-body">
      This Policy applies to all personal data collected, processed, stored, or transferred by
      Noble Guard Security Co. Ltd. in connection with:
    </p>
    <ul>
      <li>The provision of security services to clients, including the deployment of guards to client premises;</li>
      <li>The recruitment, employment, training, and management of security personnel and administrative staff;</li>
      <li>The operation of the Company&apos;s website and digital communication channels;</li>
      <li>The management of contractual relationships with clients, vendors, and business partners;</li>
      <li>Surveillance operations, CCTV monitoring, and access control activities conducted in the course of service delivery;</li>
      <li>All regulatory, compliance, and reporting obligations of the Company.</li>
    </ul>
    <p className="priv-body">
      This Policy applies to all individuals who interact with the Company in any capacity,
      including clients and their staff, employees and job applicants, business partners and
      suppliers, visitors to the Company&apos;s website, and any person whose personal data is
      processed by the Company in the course of its operations.
    </p>

    <h2>3. Categories of personal data collected</h2>
    <h3>3.1 Identity and contact information</h3>
    <p className="priv-body">
      The Company collects the following categories of personal data from clients, employees, and
      third parties:
    </p>
    <ul>
      <li>Full legal name, title, and date of birth;</li>
      <li>Residential and business address;</li>
      <li>Email address and telephone numbers;</li>
      <li>Government-issued identification documents (National Identity Number, International Passport, Driver&apos;s Licence);</li>
      <li>Next of kin information and emergency contact details;</li>
      <li>Passport photographs and profile images.</li>
    </ul>

    <h3>3.2 Employment and background data</h3>
    <p className="priv-body">
      For the purpose of deploying qualified and vetted security personnel, the Company collects
      and processes the following in respect of its employees and applicants:
    </p>
    <ul>
      <li>Employment history, professional qualifications, and references;</li>
      <li>Security licensing and certification records;</li>
      <li>Criminal background check results and police clearance certificates;</li>
      <li>Health and medical fitness declarations, to the extent required by applicable law;</li>
      <li>Biometric data, where lawfully required for identity verification or access control;</li>
      <li>Security clearance levels and vetting records.</li>
    </ul>

    <h3>3.3 Operational and surveillance data</h3>
    <p className="priv-body">In the course of providing security services, the Company may collect and process:</p>
    <ul>
      <li>CCTV footage and video surveillance recordings obtained at or around client premises;</li>
      <li>Access control records, including entry and exit logs;</li>
      <li>Incident reports, occurrence books, and security logs;</li>
      <li>Patrol reports and duty records;</li>
      <li>Photographs and recordings obtained during security investigations.</li>
    </ul>

    <h3>3.4 Usage data and technical information</h3>
    <p className="priv-body">
      When users access the Company&apos;s website, the following information may be collected automatically:
    </p>
    <ul>
      <li>Internet Protocol (IP) address;</li>
      <li>Browser type, version, and language settings;</li>
      <li>Operating system and device information;</li>
      <li>Pages visited, hyperlinks clicked, and time spent on each page;</li>
      <li>Date and time of access;</li>
      <li>Referring URLs and search terms.</li>
    </ul>

    <h3>3.5 Sensitive personal data</h3>
    <p className="priv-body">
      Given the nature of security services, the Company may be required to process certain
      categories of sensitive personal data, including biometric identifiers, criminal record
      information, and health-related data. Such data is processed strictly on the basis of legal
      obligation, legitimate security interest, or explicit consent of the data subject, and is
      subject to enhanced protective measures as prescribed under the NDPA and applicable
      regulations.
    </p>

    <h2>4. Cookies and tracking technologies</h2>
    <p className="priv-body">
      The Company&apos;s website uses cookies and similar tracking technologies to enhance user
      experience, ensure website security, and analyse website performance. The types of cookies
      deployed by the Company are as follows:
    </p>
    <p className="priv-body">
      <strong>Session Cookies:</strong> These are essential to the operation of the website and
      enable core functionality such as secure navigation and session management. They cannot be
      disabled without impairing website function.
    </p>
    <p className="priv-body">
      <strong>Security Cookies:</strong> These are used to detect and prevent fraudulent activity,
      enforce security protocols, and protect the integrity of the Company&apos;s digital platforms.
    </p>
    <p className="priv-body">
      <strong>Analytics Cookies:</strong> These collect anonymised data regarding how visitors
      interact with the website, including which pages are visited most frequently. This data is
      used exclusively to improve website performance.
    </p>
    <p className="priv-body">
      <strong>Preference Cookies:</strong> These remember user settings and preferences, such as
      language selection, to personalise the browsing experience.
    </p>
    <p className="priv-body">
      Users may manage or disable cookies through their browser settings. Please note, however, that
      disabling certain cookies may limit access to some features of the Company&apos;s website.
      The Company does not use cookies to collect personal data for commercial advertising purposes.
    </p>

    <h2>5. Legal basis for processing personal data</h2>
    <p className="priv-body">
      The Company processes personal data only where it has a lawful basis to do so. The legal bases
      upon which the Company relies include the following:
    </p>
    <p className="priv-body">
      <strong>Consent:</strong> Where the data subject has given clear and unambiguous consent to the
      processing of their personal data for one or more specific purposes.
    </p>
    <p className="priv-body">
      <strong>Contractual Necessity:</strong> Where processing is necessary for the performance of a
      contract to which the data subject is a party, or in order to take steps at the request of the
      data subject prior to entering into a contract.
    </p>
    <p className="priv-body">
      <strong>Legal Obligation:</strong> Where processing is required to comply with a legal obligation
      to which the Company is subject, including obligations under Nigerian labour law, tax law, and
      security industry regulations.
    </p>
    <p className="priv-body">
      <strong>Vital Interests:</strong> Where processing is necessary to protect the vital interests of
      the data subject or another natural person.
    </p>
    <p className="priv-body">
      <strong>Legitimate Interests:</strong> Where processing is necessary for the purposes of the
      legitimate interests pursued by the Company, provided that such interests are not overridden
      by the rights and freedoms of the data subject.
    </p>

    <h2>6. Purposes of data processing</h2>
    <p className="priv-body">The Company processes personal data for the following specific and lawful purposes:</p>
    <ul>
      <li>Provision, management, and improvement of professional security services;</li>
      <li>Recruitment, vetting, deployment, training, and administration of security personnel;</li>
      <li>Conducting security risk assessments, threat evaluations, and incident investigations;</li>
      <li>Monitoring of client premises through CCTV and surveillance systems in accordance with agreed service specifications;</li>
      <li>Fulfilment of obligations under service contracts, including reporting and documentation;</li>
      <li>Prevention, detection, and reporting of criminal activity, fraud, or security breaches;</li>
      <li>Compliance with applicable laws, regulatory requirements, and court orders;</li>
      <li>Communication with clients, employees, and regulatory authorities;</li>
      <li>Processing of payments, invoicing, and financial record-keeping;</li>
      <li>Maintaining the security and integrity of the Company&apos;s digital infrastructure;</li>
      <li>Pursuit or defence of legal claims.</li>
    </ul>

    <h2>7. Data retention</h2>
    <p className="priv-body">
      The Company retains personal data only for as long as is necessary to fulfil the purposes for
      which it was collected, or as required by law, contract, or legitimate business need. We may
      retain your personal data for an additional period to the extent that deletion would require us
      to overwrite our automated disaster recovery backup systems or to the extent we deem it necessary
      to assert or defend legal claims during any relevant retention period. The following retention
      principles apply:
    </p>
    <ul>
      <li>Client and contractual data is retained for a minimum of six (6) years from the date of termination of the relevant contract, in accordance with applicable limitation of actions legislation;</li>
      <li>CCTV and surveillance footage is retained for a period of thirty (30) days, unless the footage is required in connection with an ongoing incident, investigation, or legal proceeding, in which case it shall be retained until the matter is fully resolved;</li>
      <li>Incident reports and security logs are retained for as long as may be required;</li>
      <li>Website usage data is retained for a period not exceeding twelve (12) months from the date of collection or for as long as may be required.</li>
    </ul>

    <h2>8. Disclosure and sharing of personal data</h2>
    <p className="priv-body">
      The Company does not sell, rent, or trade personal data to third parties. The Company may,
      however, disclose personal data to the following categories of recipients, strictly on a
      need-to-know basis and subject to appropriate safeguards:
    </p>
    <ul>
      <li>Law enforcement agencies, regulatory bodies, and government authorities, where required by law, court order, or in connection with the prevention or detection of criminal activity;</li>
      <li>Insurance underwriters and claims handlers, in connection with the processing of insurance claims arising from incidents involving deployed personnel;</li>
      <li>Vetted third-party service providers who process personal data on behalf of the Company under written data processing agreements, including IT service providers, payroll processors, and background check agencies;</li>
      <li>Legal advisers and professional consultants retained by the Company in connection with legal proceedings or regulatory investigations;</li>
      <li>Successor entities in the event of a merger, acquisition, restructuring, or transfer of business assets, subject to the data subjects being notified of any change in data controller.</li>
    </ul>
    <p className="priv-body">
      All third parties with whom the Company shares personal data are required to maintain appropriate
      technical and organisational security measures and are prohibited from using the data for any
      purpose other than that for which it was shared.
    </p>

    <h2>9. International data transfers</h2>
    <p className="priv-body">
      Where personal data is transferred to recipients outside the Federal Republic of Nigeria, the
      Company shall ensure that such transfer is made only where:
    </p>
    <ul>
      <li>The recipient country has been assessed as providing an adequate level of data protection comparable to Nigerian standards;</li>
      <li>Appropriate safeguards have been put in place, including standard contractual clauses, binding corporate rules, or other mechanisms recognised under the NDPA;</li>
      <li>The data subject has given explicit informed consent to the transfer; or</li>
      <li>The transfer is necessary for the performance of a contract or legal obligation.</li>
    </ul>

    <h2>10. Data security</h2>
    <p className="priv-body">
      The Company maintains a comprehensive information security programme designed to protect personal
      data against unauthorised access, disclosure, alteration, loss, or destruction. The security
      measures implemented by the Company include:
    </p>
    <ul>
      <li>End-to-end encryption of data in transit and at rest using industry-standard protocols;</li>
      <li>Role-based access controls ensuring that personal data is accessible only to authorised personnel on a strict need-to-know basis;</li>
      <li>Regular security audits, vulnerability assessments, and penetration testing of digital systems;</li>
      <li>Confidentiality agreements and data protection training for all employees and contractors;</li>
      <li>Physical security controls protecting premises where personal data is stored;</li>
      <li>Incident response and data breach management procedures in accordance with NDPA requirements.</li>
    </ul>
    <p className="priv-body">
      Notwithstanding the foregoing, the Company acknowledges that no system of data transmission over
      the internet or electronic storage is entirely secure. We maintain reasonable and appropriate,
      albeit not infallible, physical, electronic and procedural safeguards intended to maintain the
      confidentiality of Personal Information, including that provided by a visitor to this website and
      provided while using other Internet Services. We do not guarantee that our safeguards will always
      work.
    </p>
    <p className="priv-body">
      We require clients and vendors to maintain data protections consistent with reasonable and
      appropriate obligations of data processors, including, where applicable, the European Union&apos;s
      data protection laws and regulations.
    </p>

    <h2>11. Rights of data subjects</h2>
    <p className="priv-body">
      Subject to the provisions of the NDPA and applicable law, all data subjects whose personal data
      is processed by the Company are entitled to exercise the following rights:
    </p>
    <p className="priv-body">
      <strong>Right to Information:</strong> The right to be informed of the collection and use of
      personal data in a clear and transparent manner.
    </p>
    <p className="priv-body">
      <strong>Right of Access:</strong> The right to obtain confirmation as to whether the Company is
      processing personal data concerning the data subject, and to access a copy of such data.
    </p>
    <p className="priv-body">
      <strong>Right to Rectification:</strong> The right to request the correction of inaccurate or
      incomplete personal data held by the Company.
    </p>
    <p className="priv-body">
      <strong>Right to Erasure:</strong> The right to request the erasure of personal data where it is
      no longer necessary for the purpose for which it was collected, subject to applicable legal
      exceptions.
    </p>
    <p className="priv-body">
      <strong>Right to Restriction:</strong> The right to request that the processing of personal data be
      restricted in certain circumstances.
    </p>
    <p className="priv-body">
      <strong>Right to Data Portability:</strong> The right to receive personal data in a structured,
      commonly used, and machine-readable format and to transmit such data to another data controller.
    </p>
    <p className="priv-body">
      <strong>Right to Object:</strong> The right to object to the processing of personal data on the
      basis of legitimate interests or direct marketing.
    </p>
    <p className="priv-body">
      <strong>Right to Withdraw Consent:</strong> The right to withdraw consent to processing at any
      time, without prejudice to the lawfulness of processing based on consent before its withdrawal.
    </p>
    <p className="priv-body">
      To exercise any of the above rights, data subjects may submit a written request to the Company&apos;s
      Data Protection Officer using the contact details set out in Section 16 of this Policy. The
      Company shall respond to all verified requests within thirty (30) days of receipt, in accordance
      with its obligations under the NDPA.
    </p>

    <h2>12. Employee and deployed personnel data</h2>
    <p className="priv-body">
      The Company collects and processes personal data relating to its employees and deployed security
      guards for purposes including recruitment and background verification, payroll and benefits
      administration, deployment scheduling and posting management, performance monitoring and
      disciplinary proceedings, regulatory compliance and licensing, and occupational health and safety
      management. All employee data is processed in accordance with the Labour Act, Cap. L1 Laws of the
      Federation of Nigeria, 2004, the NDPA, and all other applicable employment legislation. Employees
      and deployed personnel have the right to access their personal data held by the Company and to
      request corrections where necessary.
    </p>

    <h2>13. Children&apos;s privacy</h2>
    <p className="priv-body">
      The Company&apos;s Services are not directed at or intended for individuals under the age of
      eighteen (18) years. The Company does not knowingly collect, solicit, or process personal data
      from children. In the event that the Company becomes aware that personal data has been
      inadvertently collected from a child without verifiable parental or guardian consent, such data
      shall be promptly deleted from the Company&apos;s records. Any person who believes that the Company
      may have inadvertently collected data from a child is encouraged to contact the Company&apos;s
      Data Protection Officer immediately.
    </p>

    <h2>14. Regulatory compliance framework</h2>
    <p className="priv-body">
      The Company&apos;s data protection practices are designed and implemented in compliance with the
      following legislative and regulatory instruments:
    </p>
    <ul>
      <li>Nigerian Data Protection Act (NDPA), 2023;</li>
      <li>Nigeria Data Protection Regulation (NDPR), 2019 and all guidelines issued thereunder;</li>
      <li>Private Guard Companies Act, Cap. P28, Laws of the Federation of Nigeria, 2004;</li>
      <li>Labour Act, Cap. L1, Laws of the Federation of Nigeria, 2004;</li>
      <li>Evidence Act, 2011 (as amended), with respect to the admissibility of surveillance records;</li>
      <li>Cybercrimes (Prohibition, Prevention, etc.) Act, 2015;</li>
      <li>All applicable guidelines issued by the Nigeria Data Protection Commission (NDPC).</li>
    </ul>
    <p className="priv-body">
      The Company has designated a Data Protection Officer (&quot;DPO&quot;) responsible for overseeing
      compliance with this Policy and applicable data protection legislation. The DPO may be contacted
      using the details provided in Section 16 below.
    </p>

    <h2>15. Amendments to this Policy</h2>
    <p className="priv-body">
      The Company reserves the right to amend, update, or revise this Policy from time to time in
      response to changes in applicable law, regulatory guidance, business operations, or industry best
      practices. Any material changes to this Policy will be communicated to affected data subjects
      through the Company&apos;s website and, where appropriate, through direct notification. The revised
      Policy will take effect from the date specified as the new Effective Date at the top of the
      document. Data subjects are encouraged to review this Policy periodically to remain informed of
      how the Company protects their personal data. Continued use of the Company&apos;s Services following
      the publication of an updated Policy shall constitute acceptance of the revised terms.
    </p>

    <h2>16. Contact information and Data Protection Officer</h2>
    <p className="priv-body">
      All enquiries, complaints, requests to exercise data subject rights, or concerns relating to this
      Policy or the Company&apos;s data processing activities should be directed to the Company&apos;s Data
      Protection Officer at the following contact details:
    </p>
    <p className="priv-body">
      Noble Guard Security Co. Ltd.
      <br />
      Data Protection Officer
      <br />
      Email:{" "}
      <a href="mailto:hr@nobleguard.com.ng">hr@nobleguard.com.ng</a>
      <br />
      Address: Plot 1773, 7th Avenue, Festac, Amuwo-Odofin, Lagos State, Nigeria.
      <br />
      Website:{" "}
      <a href="https://www.nobleguard.com.ng" target="_blank" rel="noopener noreferrer">
        www.nobleguard.com.ng
      </a>
    </p>
    <p className="priv-body">
      This Policy has been duly approved and authorised by the Management of Noble Guard Security Co.
      Ltd.
    </p>

    <p className="upd">Last updated: 20 April 2026</p>
  </div>
);

export default PrivacyPolicyBody;
